/**
 * @file 精简教案卷打印模板
 * @description 对应客户端 KnowledgePrintTemplateLite：典例竖排讲解、巩固练习与答案。
 *
 * @whenToUse 试卷 3（paper3）的打印预览。
 * @whenNotToUse 完整教案卷与阶段卷。
 */

import { QuestionType } from '@/enum/common.ts'
import {
  buildPaperAnswerQrCodeDataUrl,
  type HandoutPaperModel,
  type PaperQuestion,
} from '@/components/pages/handout-page/print/paper-data.ts'
import { PaperPages } from '@/components/pages/handout-page/print/paper-pages.tsx'
import type { PaperPaginator } from '@/components/pages/handout-page/print/paginate.ts'
import { usePaperLayout } from '@/components/pages/handout-page/print/use-paper-layout.ts'

/**
 * 写入一组答案。
 * @param pager 分页器
 * @param title 小标题
 * @param questions 题目
 */
async function addAnswers(
  pager: PaperPaginator,
  title: string,
  questions: PaperQuestion[],
) {
  await pager.add({ type: 'answer-subtitle', data: { subTitle: title } })
  for (let index = 0; index < questions.length; index += 1) {
    const question = questions[index]
    await pager.add({
      type: 'answer-data',
      data: {
        index: index + 1,
        answerHtml: question.answerHtml,
        analysisHtml: question.analysisHtml,
      },
    })
  }
}

/**
 * 按精简教案卷顺序分页。
 * @param pager 分页器
 * @param model 试卷
 */
async function fillKnowledgeLite(pager: PaperPaginator, model: HandoutPaperModel) {
  if (model.learnWithMeList.length > 0) {
    await pager.add({
      type: 'block_title',
      data: { icon: 'genwoxue', title: '常考题型', description: '逐个击破！！！' },
    })
    for (let index = 0; index < model.learnWithMeList.length; index += 1) {
      const question = model.learnWithMeList[index]
      await pager.add({
        type: 'learn-with-me-vertical',
        isLast: index === 0,
        data: {
          index: index + 1,
          exampleHtml: question.questionHtml,
          analysisHtml: question.analysisHtml,
          options: question.type === QuestionType.SELECT ? question.options ?? null : null,
        },
      })
    }
    await pager.add({
      type: 'block_footer',
      text: '（注重解析，在不会做题的时候，解析会提供做题方法，解题思路！！！）',
    })
  }

  if (model.adaptivePracticeQuestions.length > 0) {
    await pager.add({
      type: 'block_title',
      data: { icon: 'lianxi', title: '巩固练习', description: '我要开始练习啦！' },
    })
    for (let index = 0; index < model.adaptivePracticeQuestions.length; index += 1) {
      const question = model.adaptivePracticeQuestions[index]
      await pager.add({
        type: 'adaptivePracticeQuestions',
        isLast: index === 0,
        data: {
          index: index + 1,
          question: question.questionHtml,
          needPlaceholder:
            question.type === QuestionType.APPLICATION ||
            question.type === QuestionType.SHORT_ANSWER,
          options: question.type === QuestionType.SELECT ? question.options ?? null : null,
        },
      })
    }
  }

  if (!model.printAnswers) return

  await pager.breakPage()
  await pager.add({ type: 'answer-prefix' })
  await pager.add({
    type: 'block_title',
    data: { title: '答案', description: '按模块查看本卷答案与讲解。' },
  })
  await addAnswers(pager, '例题变形答案', model.tryPracticeQuestions)
  await addAnswers(pager, '巩固练习答案', model.adaptivePracticeQuestions)

  if (model.header.paperNo) {
    const qrCodeDataUrl = await buildPaperAnswerQrCodeDataUrl(model.header.paperNo)
    await pager.add({
      type: 'paper-qr-code',
      data: { paperNo: model.header.paperNo, qrCodeDataUrl },
    })
  }
}

/**
 * 精简教案卷（试卷 3）。
 */
export function KnowledgePrintTemplateLite({
  model,
  onReady,
}: {
  /** 试卷数据 */
  model: HandoutPaperModel
  /** 分页结束 */
  onReady?: (ready: boolean) => void
}) {
  const layout = usePaperLayout('lite', model, fillKnowledgeLite, onReady)
  return (
    <PaperPages
      pages={layout.pages}
      ready={layout.ready}
      rootRef={layout.rootRef}
      containers={layout.containers}
      header={model.header}
    />
  )
}
